/**
 * Authentication service for email/password login and JWT management.
 *
 * Requirements: 16.2, 16.3, 16.4
 * - Email/password login
 * - JWT tokens in localStorage
 * - Automatic token refresh
 * - Redirect to login on refresh token expiry
 */

import { api, refreshAccessToken } from './api';
import type { AuthResponse, TelegramLoginData, TelegramOidcCallbackData, User } from '../types';

const TOKEN_REFRESH_INTERVAL = 13 * 60 * 1000; // Refresh 2 minutes before 15-min expiry

let refreshInterval: ReturnType<typeof setInterval> | null = null;

export const authService = {
  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>(
      '/auth/login/',
      { email, password },
      { auth: false, retryAuth: false },
    );
    this.setTokens(response.access, response.refresh);
    this.startAutoRefresh();
    return response;
  },

  async register(
    email: string,
    password: string,
    username?: string,
    preferred_currency?: string,
  ): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>(
      '/auth/register/',
      {
        email,
        password,
        username,
        preferred_currency,
      },
      { auth: false, retryAuth: false },
    );
    this.setTokens(response.access, response.refresh);
    this.startAutoRefresh();
    return response;
  },

  async telegramLogin(data: TelegramLoginData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>(
      '/auth/telegram/login-widget/',
      data,
      { auth: false, retryAuth: false },
    );
    this.setTokens(response.access, response.refresh);
    this.startAutoRefresh();
    return response;
  },

  async telegramOidcLogin(data: TelegramOidcCallbackData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>(
      '/auth/telegram/oidc/callback/',
      data,
      { auth: false, retryAuth: false },
    );
    this.setTokens(response.access, response.refresh);
    this.startAutoRefresh();
    return response;
  },

  setTokens(access: string, refresh: string) {
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
  },

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  },

  async logout(): Promise<void> {
    const refresh = localStorage.getItem('refresh_token');
    try {
      if (refresh) {
        await api.post('/auth/logout/', { refresh });
      }
    } catch {
      // Local logout must remain available while offline or if the token was
      // already revoked server-side.
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      this.stopAutoRefresh();
      window.location.assign('/login');
    }
  },

  async refreshToken(): Promise<boolean> {
    return refreshAccessToken();
  },

  startAutoRefresh() {
    this.stopAutoRefresh();
    refreshInterval = setInterval(() => {
      void this.refreshToken();
    }, TOKEN_REFRESH_INTERVAL);
  },

  stopAutoRefresh() {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
  },

  async getCurrentUser(): Promise<User | null> {
    if (!this.isAuthenticated()) return null;
    try {
      return null;
    } catch {
      return null;
    }
  },
};
